/**
 * Canonical print/export layout metrics.
 *
 * Single source of truth shared by the pdfmake export path
 * (`DownloadToPdfService`) and the browser print path (print templates +
 * `print-inline.css`), so the two renderers cannot drift apart again.
 *
 * Every value here is derived from what the Quill editor actually renders —
 * `node_modules/quill/dist/quill.snow.css` — because the editor is the surface
 * the staff configures, and therefore the thing the output has to match.
 *
 * All figures were verified by measurement (see
 * PLAN_print-export-layout-consistency-MEASURED.md):
 *   - paragraph advance, editor  : 22.72px  = 17.04pt
 *   - paragraph advance, export  : 17.02pt at PDF_LINE_HEIGHT (−0.1%)
 *   - indent, editor             : 3em per level  (0 / 48 / 96px)
 *   - indent, export             : 0 / 36 / 72 / 108pt at 12pt base
 */

import { PrintLetterheadMode } from '@app/shared/constants/settings.constants';

/**
 * pdfmake line-height that reproduces the editor's `line-height: 1.42`.
 *
 * NOTE: this is NOT 1.42. pdfmake multiplies the font's ascent+descent, not
 * `fontSize`, so CSS 1.42 and pdfmake 1.42 are different quantities. Measured
 * calibration against Chromium at 12pt:
 *
 *   pdfmake 1.00 -> 14.06pt (−17.5%)     pdfmake 1.21 -> 17.02pt (−0.1%)  <- match
 *   pdfmake 1.20 -> 16.88pt ( −1.0%)     pdfmake 1.42 -> 19.97pt (+17.2%)
 *
 * Do not "correct" this to 1.42 — that reintroduces a 17% vertical stretch.
 */
export const PDF_LINE_HEIGHT = 1.21;

/** Quill: `.ql-editor { line-height: 1.42 }` — the CSS-side value, for print templates. */
export const CSS_LINE_HEIGHT = 1.42;

/** Quill: `.ql-editor .ql-indent-N { padding-left: 3N em }`. */
export const INDENT_PER_LEVEL_EM = 3;

/** Quill: `.ql-editor ol, .ql-editor ul { padding-left: 1.5em }`. */
export const LIST_PADDING_LEFT_EM = 1.5;

/** Base font size in pt per paper size (matches the previous `defaultStyle`). */
export const BASE_FONT_PT = { letter: 12, half: 11 };

/**
 * Heading scale, expressed as a multiple of the base font size.
 *
 * These are the browser's default `h1..h6` sizes, which is what the editor
 * shows (Quill only zeroes heading margins, it does not restyle sizes).
 * html-to-pdfmake's own defaults are absolute (24/22/20/18/16/14pt) and so
 * ignore the document's base size entirely.
 */
export const HEADING_SCALE = { h1: 2, h2: 1.5, h3: 1.17, h4: 1, h5: 0.83, h6: 0.67 };

/** Uniform page margins, in pt. Valid only once nothing variable-height sits in the header/footer. */
export const PAGE_MARGINS = {
  side: { letter: 30, half: 24 },
  top: 24,
  bottom: 28
};

/**
 * Column spec for the 7-column treatment-plan table.
 *
 * On half-letter the text column is only 348pt (396 − 2×24), and seven
 * `'auto'` columns do not fit. pdfmake squeezes auto columns toward their
 * MINIMUM width — the longest unbreakable token plus 8pt of default cell
 * padding — and when even those minimums exceed the page it stops squeezing and
 * draws past the right edge rather than clipping. Four currency columns whose
 * values ("12,345.00") cannot be broken, plus a `Discount %` header that cannot
 * be broken, plus 56pt of padding across seven cells, is over budget.
 *
 * Measured with a representative plan: the auto widths overflow by 11.7pt;
 * the fixed widths below fit with 3pt to spare.
 *
 * Fixed numeric columns are deliberate. Shrinking the font or the padding alone
 * also fits today, but leaves the columns free to grow with the data — one
 * six-figure amount and the overflow returns. Capping them and letting
 * Procedure absorb the remainder via `'*'` cannot overflow whatever the values.
 *
 * Letter has 552pt and is unaffected, so it keeps the auto widths.
 */
export const TREATMENT_PLAN_TABLE = {
  /**
   * Appt · Tooth # · Procedure · Unit Price · Subtotal · Discount · Est. Cost
   *
   * Budget: 348pt text column − 7 cells × 4pt padding = 320pt to divide.
   *
   * The three currency columns are sized so a six-figure peso amount stays on
   * one line at 9pt (` 150,000.00` measures 46.8pt, so 51pt with padding). A
   * wrapped amount reads as broken data in a way wrapped prose does not, so
   * currency gets the guaranteed space and Procedure absorbs the remainder.
   *
   * Tooth # is deliberately narrow: `12, 13, 14` needs 43pt to stay on one line
   * but it is a comma-separated list and wraps cleanly, so it gives up 17pt to
   * Procedure. Procedure ends up at ~77pt and wraps like the prose it is.
   *
   * Seven columns is genuinely too many for a 5.5in sheet — this is the least
   * bad division, not a comfortable one. If the output still reads as cramped,
   * the real fix is dropping a column on half-letter (Unit Price and Subtotal
   * are identical whenever quantity is 1), and that is a product decision.
   */
  halfWidths: [24, 26, '*', 51, 51, 40, 51],
  letterWidths: ['auto', 'auto', '*', 'auto', 'auto', 'auto', 'auto'],
  /** 4-column variant (no per-row price) fits on both sizes. */
  compactWidths: ['auto', 'auto', '*', 'auto'],
  halfFontSize: 9,
  halfCellPadding: 2
};

/** pdfmake table layout that trims cell padding, for the half-letter table above. */
export const tightCellPaddingLayout = () => ({
  paddingLeft: () => TREATMENT_PLAN_TABLE.halfCellPadding,
  paddingRight: () => TREATMENT_PLAN_TABLE.halfCellPadding
});

/** Footer page-number format. Ticket asks for `1/5`, not `Page 1 of 5`. */
export const pageNumberLabel = (currentPage: number, pageCount: number): string =>
  `${currentPage}/${pageCount}`;

/**
 * Per-export overrides for the account-level print settings.
 *
 * Every key is optional, and an omitted key falls back to the account setting —
 * so a caller that passes nothing behaves exactly as it did before this type
 * existed. This is what lets the staff choose on the export dialog itself
 * without changing the clinic's saved default.
 */
export interface PrintExportOptions {
  /**
   * Where the letterhead and signature block go for this one document.
   * Falls back to the `print_letterhead_mode` account setting.
   *
   * Does NOT affect page numbering — `n/N` is drawn in every mode.
   */
  letterheadMode?: PrintLetterheadMode;
}

/**
 * Legacy markup cleanup.
 *
 * `resolveQuillIndent()` used to write an inline `margin-left: <3N>em` onto
 * `.ql-indent-N` elements before saving. Combined with the CSS indent rules that
 * produces exactly double the intended indent in browser print (measured:
 * 0/96/192px against the editor's 0/48/96px). Records already saved still carry
 * it, so it is stripped at render time in both pipelines rather than migrated.
 */
export const LEGACY_INDENT_MARGIN_PATTERN = /margin-left\s*:\s*[\d.]+\s*em\s*;?/gi;
